package com.bikompanion.ui.composables

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.FilterChip
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.bikompanion.domain.BikeIssue
import com.bikompanion.ui.preview.PreviewSurface
import com.bikompanion.ui.strings.LocalStrings

/**
 * The issue chips, appearing and disappearing as the electric-only ones come and go.
 *
 * Deliberately unanimated. Animating the transition means a chip has to outlive its issue, which
 * takes a parallel list of display items, a per-chip animation and a lookahead pass to move the
 * chips after it — and every version of that flickered on device.
 */
@Composable
internal fun IssueChips(
    issues: List<BikeIssue>,
    selectedIssues: Set<BikeIssue>,
    enabled: Boolean,
    onIssueToggle: (BikeIssue) -> Unit,
) {
    FlowRow(
        horizontalArrangement = Arrangement.spacedBy(4.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        issues.forEach { issue ->
            key(issue) {
                FilterChip(
                    selected = issue in selectedIssues,
                    onClick = { onIssueToggle(issue) },
                    label = { Text(issue.label()) },
                    enabled = enabled,
                )
            }
        }
    }
}

@Composable
private fun BikeIssue.label(): String =
    LocalStrings.current.let { strings ->
        when (this) {
            BikeIssue.FlatTire -> strings.issueFlatTire
            BikeIssue.FrontBrakeNotWorking -> strings.issueFrontBrake
            BikeIssue.RearBrakeNotWorking -> strings.issueRearBrake
            BikeIssue.ChainSlipping -> strings.issueChain
            BikeIssue.GearsNotShifting -> strings.issueGears
            BikeIssue.SeatDamagedOrMissing -> strings.issueSeat
            BikeIssue.LightsNotWorking -> strings.issueLights
            BikeIssue.LockOrBasketDamaged -> strings.issueLockOrBasket
            BikeIssue.FrameDamage -> strings.issueFrame
            BikeIssue.ElectricMotorNotWorking -> strings.issueMotor
            BikeIssue.BatteryNotCharging -> strings.issueBattery
            BikeIssue.Other -> strings.issueOther
        }
    }

@Preview
@Composable
private fun IssueChipsPreview() {
    val selected = setOf(BikeIssue.FlatTire, BikeIssue.ChainSlipping)
    PreviewSurface {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            IssueChips(issues = BikeIssue.entries, selectedIssues = emptySet(), enabled = true, onIssueToggle = {})
            IssueChips(issues = BikeIssue.entries, selectedIssues = selected, enabled = true, onIssueToggle = {})
            IssueChips(issues = BikeIssue.entries, selectedIssues = selected, enabled = false, onIssueToggle = {})
        }
    }
}
